import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mountain, Phone, ArrowRight, Home, Compass, Calendar, MapPin, 
  Instagram, Check, ArrowLeft, Upload, Image, X, Loader2, Camera,
  Building, Star, Users, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const supplierTypes = [
  { id: 'host', label: 'Unique Stays', icon: Home, description: 'List your property, homestay, or resort' },
  { id: 'trek_organizer', label: 'Treks & Hikes', icon: Compass, description: 'Organize trekking expeditions and hikes' },
  { id: 'event_organizer', label: 'Events & Festivals', icon: Calendar, description: 'Host festivals, rallies, or cultural events' }
];

const SupplierOnboarding = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('supplier_token');
    if (token) {
      navigate('/supplier/dashboard');
    }
  }, [navigate]);

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleSendOTP = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    // For signup, require type selection
    if (mode === 'signup' && selectedTypes.length === 0) {
      toast.error('Please select what you want to offer');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/supplier/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.startsWith('+91') ? phone : `+91${phone}`,
          supplier_types: mode === 'signup' ? selectedTypes : [],
          business_name: mode === 'signup' ? (businessName || null) : null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('OTP sent to your phone!');
        setOtpSent(true);
        setStep(2);
        if (data.otp_for_testing) {
          toast.info(`Test OTP: ${data.otp_for_testing}`, { duration: 10000 });
        }
      } else {
        toast.error(data.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/supplier/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.startsWith('+91') ? phone : `+91${phone}`,
          otp
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('supplier_token', data.token);
        toast.success(data.is_new ? 'Welcome to GoHimalayan!' : 'Welcome back!');
        navigate('/supplier/dashboard');
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            <span className="text-sm text-gray-500">Partner Portal</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Benefits */}
          <div className="hidden md:block">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {mode === 'login' ? 'Welcome back, Partner!' : 'Join the Himalayan Network'}
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              {mode === 'login' 
                ? 'Sign in to manage your listings, bookings, and grow your business.'
                : 'List your property, treks, or events. Reach travelers from around the world.'
              }
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">5000+ Travelers</h3>
                  <p className="text-sm text-gray-500">Active community seeking Himalayan experiences</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Grow Your Revenue</h3>
                  <p className="text-sm text-gray-500">Low commission rates starting at 5%</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Premium Visibility</h3>
                  <p className="text-sm text-gray-500">Get featured on our curated collections</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Login/Signup Toggle */}
            <div className="flex bg-gray-100 rounded-full p-1 mb-8">
              <button
                onClick={() => { setMode('login'); setStep(1); setOtpSent(false); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all ${
                  mode === 'login' 
                    ? 'bg-white shadow text-gray-900' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Log In
              </button>
              <button
                onClick={() => { setMode('signup'); setStep(1); setOtpSent(false); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all ${
                  mode === 'signup' 
                    ? 'bg-white shadow text-gray-900' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Step Indicator (Signup only) */}
            {mode === 'signup' && (
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 1 ? 'bg-[#2C5F8D] text-white' : 'bg-gray-200 text-gray-500'
                }`}>1</div>
                <div className={`w-16 h-1 rounded ${step >= 2 ? 'bg-[#2C5F8D]' : 'bg-gray-200'}`} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 2 ? 'bg-[#2C5F8D] text-white' : 'bg-gray-200 text-gray-500'
                }`}>2</div>
              </div>
            )}

            {/* LOGIN MODE */}
            {mode === 'login' && !otpSent && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Partner Login</h2>
                <p className="text-gray-500 mb-6">Enter your registered phone number</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">+91</span>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="9876543210"
                        className="pl-14 h-12 text-lg"
                        maxLength={10}
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleSendOTP}
                    disabled={loading || phone.length < 10}
                    className="w-full h-12 bg-[#2C5F8D] hover:bg-[#1e4570] text-lg"
                    data-testid="send-otp-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Get OTP <ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                </div>

                <p className="text-center text-sm text-gray-500 mt-6">
                  New partner?{' '}
                  <button onClick={() => setMode('signup')} className="text-[#2C5F8D] font-medium hover:underline">
                    Create an account
                  </button>
                </p>
              </div>
            )}

            {/* SIGNUP MODE - Step 1 */}
            {mode === 'signup' && step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Become a Partner</h2>
                <p className="text-gray-500 mb-6">What would you like to offer?</p>

                {/* Supplier Types */}
                <div className="space-y-3 mb-6">
                  {supplierTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => toggleType(type.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        selectedTypes.includes(type.id)
                          ? 'border-[#2C5F8D] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`type-${type.id}`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        selectedTypes.includes(type.id) ? 'bg-[#2C5F8D]' : 'bg-gray-100'
                      }`}>
                        <type.icon className={`w-6 h-6 ${
                          selectedTypes.includes(type.id) ? 'text-white' : 'text-gray-500'
                        }`} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-semibold text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </div>
                      {selectedTypes.includes(type.id) && (
                        <div className="w-6 h-6 rounded-full bg-[#2C5F8D] flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Business Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g., Himalayan Homestays"
                    className="h-12"
                    data-testid="business-name-input"
                  />
                </div>

                {/* Phone */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">+91</span>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="9876543210"
                      className="pl-14 h-12 text-lg"
                      maxLength={10}
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleSendOTP}
                  disabled={loading || phone.length < 10 || selectedTypes.length === 0}
                  className="w-full h-12 bg-[#2C5F8D] hover:bg-[#1e4570] text-lg"
                  data-testid="send-otp-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>

                <p className="text-center text-sm text-gray-500 mt-6">
                  Already a partner?{' '}
                  <button onClick={() => setMode('login')} className="text-[#2C5F8D] font-medium hover:underline">
                    Log in
                  </button>
                </p>
              </div>
            )}

            {/* OTP VERIFICATION (Both modes) */}
            {otpSent && step === 2 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Phone</h2>
                  <p className="text-gray-500">Enter the 6-digit code sent to +91 {phone}</p>
                </div>

                <div className="space-y-4">
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    className="text-center text-2xl tracking-[0.5em] h-14 font-mono"
                    maxLength={6}
                    data-testid="otp-input"
                  />

                  <Button 
                    onClick={handleVerifyOTP}
                    disabled={loading || otp.length !== 6}
                    className="w-full h-12 bg-[#2C5F8D] hover:bg-[#1e4570] text-lg"
                    data-testid="verify-otp-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify & Continue <Check className="w-5 h-5 ml-2" /></>}
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <button 
                      onClick={() => { setStep(1); setOtpSent(false); setOtp(''); }}
                      className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <ArrowLeft className="w-4 h-4" /> Change number
                    </button>
                    <button 
                      onClick={handleSendOTP}
                      className="text-[#2C5F8D] hover:underline"
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400 mb-4">Trusted by hosts across</p>
          <div className="flex items-center justify-center gap-8 text-gray-500">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> India</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Nepal</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Bhutan</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Tibet</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierOnboarding;
